/**
 * Workflow step reorder, copy, and paste helpers for the automation builder.
 */

export const TRIGGER_STEP_TYPES = new Set(['trigger']);

export function isTriggerStep(step) {
  return TRIGGER_STEP_TYPES.has(step?.type);
}

export function getMovingIndices(steps, movingIds) {
  return movingIds
    .map((id) => steps.findIndex((s) => s.id === id))
    .filter((index) => index >= 0)
    .sort((a, b) => a - b);
}

export function reindexWorkflowSteps(steps) {
  return steps.map((step, index) => ({
    ...step,
    order: index + 1,
  }));
}

/**
 * @param {Array} steps
 * @param {string[]} movingIds
 * @param {number} targetIndex - insertion index in the list before removal (0..steps.length)
 */
export function canDropStep(steps, movingIds, targetIndex) {
  if (!Array.isArray(steps) || !movingIds?.length) return false;
  if (targetIndex < 0 || targetIndex > steps.length) return false;

  const indices = getMovingIndices(steps, movingIds);
  if (indices.length !== movingIds.length) return false;

  for (const id of movingIds) {
    const step = steps.find((s) => s.id === id);
    if (isTriggerStep(step)) return false;
  }

  if (movingIds.length === 1) {
    const fromIndex = indices[0];
    const adjustedTarget = targetIndex > fromIndex ? targetIndex - 1 : targetIndex;
    return adjustedTarget !== fromIndex;
  }

  const startIndex = indices[0];
  const blockLength = movingIds.length;
  const isContiguous = indices.every((index, offset) => index === startIndex + offset);
  if (!isContiguous) return false;

  const adjustedTarget = targetIndex > startIndex ? targetIndex - blockLength : targetIndex;
  if (adjustedTarget === startIndex) return false;
  return adjustedTarget >= 0 && adjustedTarget <= steps.length - blockLength;
}

export function moveStep(steps, stepId, targetIndex) {
  if (!canDropStep(steps, [stepId], targetIndex)) {
    return steps;
  }

  const fromIndex = steps.findIndex((s) => s.id === stepId);
  const step = steps[fromIndex];
  const without = steps.filter((_, index) => index !== fromIndex);
  const adjustedTarget = targetIndex > fromIndex ? targetIndex - 1 : targetIndex;

  const next = [...without];
  next.splice(adjustedTarget, 0, step);
  return reindexWorkflowSteps(next);
}

export function moveStepBlock(steps, startStepId, targetIndex) {
  const startIndex = steps.findIndex((s) => s.id === startStepId);
  if (startIndex < 0) return steps;

  const block = steps.slice(startIndex);
  const movingIds = block.map((s) => s.id);
  if (!canDropStep(steps, movingIds, targetIndex)) {
    return steps;
  }

  const without = steps.slice(0, startIndex);
  const adjustedTarget = targetIndex > startIndex ? targetIndex - block.length : targetIndex;

  const next = [...without];
  next.splice(adjustedTarget, 0, ...block);
  return reindexWorkflowSteps(next);
}

export function copyStep(step) {
  return JSON.parse(JSON.stringify(step));
}

export function generateNewStepIds(block) {
  const base = Date.now();
  return block.map((step, index) => ({
    ...copyStep(step),
    id: `step_${base}_${index}`,
  }));
}

export function copyStepBlock(steps, startStepId) {
  const startIndex = steps.findIndex((s) => s.id === startStepId);
  if (startIndex < 0) return [];
  return steps.slice(startIndex).map(copyStep);
}

export function pasteStepBlock(steps, block, targetIndex) {
  if (!block?.length) return steps;
  if (targetIndex < 0 || targetIndex > steps.length) return steps;

  const pasted = generateNewStepIds(block);
  const next = [...steps];
  next.splice(targetIndex, 0, ...pasted);
  return reindexWorkflowSteps(next);
}

/** Insert a deep-cloned copy of the step directly below it (builder duplicate behavior). */
export function duplicateStepAfter(steps, stepId) {
  const index = steps.findIndex((step) => step.id === stepId);
  if (index < 0) return { steps, duplicatedStep: null };

  const [duplicatedStep] = generateNewStepIds([copyStep(steps[index])]);
  const next = [...steps];
  next.splice(index + 1, 0, duplicatedStep);
  return {
    steps: reindexWorkflowSteps(next),
    duplicatedStep,
  };
}

export function getStepLabel(step) {
  return step?.label || step?.name || step?.config?.actionName || step?.type || 'Action';
}

export function buildMovingIds(steps, startStepId, includeFollowing = false) {
  const startIndex = steps.findIndex((s) => s.id === startStepId);
  if (startIndex < 0) return [];
  if (!includeFollowing) return [startStepId];
  return steps.slice(startIndex).map((s) => s.id);
}
